import { useState, useEffect, useRef } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import InfiniteScroll from 'react-infinite-scroller';

import Button from 'src/components/NewButton';
import Checkbox from 'src/components/Checkbox';
import { useClickAway } from 'src/hooks/useClickAway';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      backgroundColor: '#fff',
      borderRadius: 8,
      boxShadow:
        '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
      padding: 24,
      width: 500,
      maxHeight: 600,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      marginBottom: 16,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      marginBottom: 12,
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      flex: 1,
      fontSize: 14,
    },
    options: {
      flex: 1,
      overflowY: 'auto',
      maxHeight: 400,
    },
    option: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 0',
    },
    actions: {
      display: 'flex',
      gap: 8,
      marginTop: 16,
      justifyContent: 'flex-end',
    },
  })
);

interface GenreSelectorProps {
  open: boolean;
  onClose: () => void;
  genres: string[];
  selectedGenres: string[];
  onSelect: (genres: string[]) => void;
}

export const GenreSelector = ({
  open,
  onClose,
  genres,
  selectedGenres,
  onSelect,
}: GenreSelectorProps) => {
  const classes = useStyles();
  const [searchKey, setSearchKey] = useState('');
  const [selectedOptions, setSelectedOptions] =
    useState<string[]>(selectedGenres);
  const [records, setRecords] = useState(100);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (open) {
      setSearchKey('');
      setSelectedOptions(selectedGenres);
      setRecords(100);
      setHasMore(true);
    }
  }, [open, selectedGenres]);

  const filteredGenres = genres.filter(
    (item: string) =>
      !searchKey ||
      (item && item.toLowerCase().includes(searchKey.toLowerCase()))
  );

  const loadMore = () => {
    if (records >= filteredGenres.length) {
      setHasMore(false);
    } else {
      setTimeout(() => {
        setRecords(records + 100);
      }, 500);
    }
  };

  const handleSave = () => {
    onSelect(selectedOptions);
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modal}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box className={classes.paper}>
        <Box className={classes.header}>
          <Typography variant='h6' style={{ fontSize: 18, fontWeight: 600 }}>
            Select Genres
          </Typography>
        </Box>
        <Box className={classes.searchContainer}>
          <SearchIcon style={{ color: '#667085' }} />
          <input
            type='text'
            placeholder='Search genres...'
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className={classes.searchInput}
          />
        </Box>
        <Box className={classes.options}>
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={hasMore}
            loader={<div key={0}>Loading...</div>}
            useWindow={false}
          >
            {filteredGenres.slice(0, records).map((genre) => (
              <Box key={genre} className={classes.option}>
                <Checkbox
                  name={genre}
                  label=''
                  checked={selectedOptions.includes(genre)}
                  onChange={() => {
                    if (selectedOptions.includes(genre)) {
                      setSelectedOptions(
                        selectedOptions.filter((g) => g !== genre)
                      );
                    } else {
                      setSelectedOptions([...selectedOptions, genre]);
                    }
                  }}
                />
                <Typography style={{ fontSize: 14 }}>{genre}</Typography>
              </Box>
            ))}
          </InfiniteScroll>
        </Box>
        <Box className={classes.actions}>
          <Button
            title='Cancel'
            variant='outlined'
            height={36}
            onClick={onClose}
          />
          <Button title='Select' height={36} onClick={handleSave} />
        </Box>
      </Box>
    </Modal>
  );
};
